import React, { Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import CompanyHomeRedirect from 'views/admin/main/empresa/CompanyHomeRedirect';
import SignInDefault from 'views/auth/signIn/SignInDefault';
import SignUpDefault from 'views/auth/signUp/SignUpDefault';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { ROUTES } from 'constants/routes';
import RouteLoadingFallback from 'components/navigation/RouteLoadingFallback';
import { getRelyNavigationIcon } from 'components/navigation/relyNavigationIcons';
import {
  CLIENT_ROUTE_MANIFEST,
  getClientRouterPath,
} from 'navigation/clientRouteManifest';
import { isRelyCommandPreviewEnabled } from 'config/relyCommandPreview';

const UserNew = React.lazy(
  lazyWithRetry(() => import('views/admin/main/users/newUser'), 'user-new')
);
const CompanyProfileDetail = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresas/CompanyProfileDetail'),
    'company-profile-detail'
  )
);
const HelpCenter = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/support/HelpCenter'),
    'help-center'
  )
);
const NotificationsPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/NotificationsPage'),
    'notifications-page'
  )
);
const DocumentsPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/DocumentsPage'),
    'documents-page'
  )
);
const PerksPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/PerksPage'),
    'perks-page'
  )
);
const MemberChangesPage = React.lazy(
  lazyWithRetry(
    () =>
      import('views/admin/main/empresa/services').then((module) => ({
        default: module.MemberChangesPage,
      })),
    'member-changes-page'
  )
);
const EinRequestPage = React.lazy(
  lazyWithRetry(
    () =>
      import('views/admin/main/empresa/services').then((module) => ({
        default: module.EinRequestPage,
      })),
    'ein-request-page'
  )
);
const ServiceRequestsPage = React.lazy(
  lazyWithRetry(
    () =>
      import('views/admin/main/empresa/services').then((module) => ({
        default: module.ServiceRequestsPage,
      })),
    'service-requests-page'
  )
);
const AccountSettings = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/account/settings'),
    'account-settings'
  )
);
const ReferralsPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/referrals'),
    'customer-referrals'
  )
);
const EmpresasMain = React.lazy(
  lazyWithRetry(() => import('views/admin/main/empresas'), 'empresas-main')
);
const CompanyDashboard = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/CompanyDashboard'),
    'company-dashboard'
  )
);
const FormationPaymentPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/FormationPaymentPage'),
    'formation-payment-page'
  )
);
const BankAccountPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/BankAccountPage'),
    'bank-account-page'
  )
);
const LiliOnboardingPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/LiliOnboardingPage'),
    'lili-onboarding-page'
  )
);
const RelyCommandPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/Command'),
    'rely-command-page'
  )
);
const VisualDirectionSamples = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/design/VisualDirectionSamples'),
    'visual-direction-samples'
  )
);
const TaxesPage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/TaxesPage'),
    'taxes-page'
  )
);
const MonthlyClosePage = React.lazy(
  lazyWithRetry(
    () => import('views/admin/main/empresa/MonthlyClosePage'),
    'monthly-close-page'
  )
);
const ImportWizard = React.lazy(
  lazyWithRetry(
    () =>
      import('views/admin/main/import').then((module) => ({
        default: module.ImportWizard,
      })),
    'import-wizard'
  )
);
const ImportsListPage = React.lazy(
  lazyWithRetry(
    () =>
      import('views/admin/main/import').then((module) => ({
        default: module.ImportsListPage,
      })),
    'imports-list-page'
  )
);

const lazyElement = (Component) => (
  <Suspense fallback={<RouteLoadingFallback />}>
    <Component />
  </Suspense>
);

function RelyCommandRouteGate() {
  const { companyId } = useParams();

  if (!isRelyCommandPreviewEnabled()) {
    return <Navigate to={ROUTES.COMPANY_DASHBOARD(companyId)} replace />;
  }

  return lazyElement(RelyCommandPage);
}

const componentById = {
  signIn: <SignInDefault />,
  signUp: <SignUpDefault />,
  companyHomeRedirect: <CompanyHomeRedirect />,
  userNew: lazyElement(UserNew),
  importWizard: lazyElement(ImportWizard),
  importsList: lazyElement(ImportsListPage),
  companies: lazyElement(EmpresasMain),
  designSamples: lazyElement(VisualDirectionSamples),
  companyProfile: lazyElement(CompanyProfileDetail),
  dashboard: lazyElement(CompanyDashboard),
  formationPayment: lazyElement(FormationPaymentPage),
  notifications: lazyElement(NotificationsPage),
  notificationsRedirect: (
    <CompanyHomeRedirect routeBuilder={ROUTES.COMPANY_NOTIFICATIONS} />
  ),
  documents: lazyElement(DocumentsPage),
  banking: lazyElement(BankAccountPage),
  lili: lazyElement(LiliOnboardingPage),
  command: <RelyCommandRouteGate />,
  invoicesCommand: lazyElement(RelyCommandPage),
  taxes: lazyElement(TaxesPage),
  monthlyClose: lazyElement(MonthlyClosePage),
  memberChanges: lazyElement(MemberChangesPage),
  einRequest: lazyElement(EinRequestPage),
  serviceRequests: lazyElement(ServiceRequestsPage),
  perks: lazyElement(PerksPage),
  support: lazyElement(HelpCenter),
  referrals: lazyElement(ReferralsPage),
  settings: lazyElement(AccountSettings),
};

const routes = CLIENT_ROUTE_MANIFEST.map((route) => {
  const Icon = route.iconKey ? getRelyNavigationIcon(route.iconKey) : null;
  const component = componentById[route.componentId];

  if (!component) {
    throw new Error(`Missing component for client route: ${route.id}`);
  }

  return {
    id: route.id,
    name: route.fallbackName,
    nameKey: route.labelKey,
    path: getClientRouterPath(route.id),
    layout: `/${route.layout}`,
    icon: Icon ? (
      <Icon className="h-5 w-5 text-inherit" aria-hidden="true" />
    ) : undefined,
    component,
    showInNavbar: route.id === 'create_company',
    showInSidebar: route.id === 'create_company',
  };
});

export default routes;
